import { useEffect } from 'react'
import throttle from 'lodash.throttle'
import { useTheme } from '@mui/material/styles'
import apiClient from '../../../lib/util/api-client'
import { MetricStatusType } from '../../../types/Metrics'

const HEARTBEAT_INTERVAL = 5 * 60 * 1000

// Use only one connection across all usage of the hook.
let heartbeat: ReturnType<typeof setInterval> | null = null
let mountedCount = 0
let start = () => {}
let stop = () => {}
let reactToChange = () => {}

export const useMetricServerConnection = (resortId: string) => {
  useEffect(() => {
    if (!resortId) {
      return
    }

    mountedCount++

    if (mountedCount === 1) {
      const join = () => apiClient.io.emit('analytics join', { resortId })
      const leave = () => apiClient.io.emit('analytics leave', { resortId })

      start = () => {
        if (!heartbeat) {
          join()
          heartbeat = setInterval(join, HEARTBEAT_INTERVAL)
        }
      }

      stop = () => {
        if (heartbeat) {
          clearInterval(heartbeat)
          heartbeat = null
        }
        leave()
      }

      reactToChange = throttle(
        () => {
          if (document.visibilityState === 'visible') {
            start()
          } else {
            stop()
          }
        },
        100,
        { trailing: true }
      )

      apiClient.io.on('connect', reactToChange)
      document.addEventListener('visibilitychange', reactToChange)
      reactToChange()
    }

    return () => {
      mountedCount--
      if (mountedCount === 0) {
        apiClient.io.off('connect', reactToChange)
        document.removeEventListener('visibilitychange', reactToChange)
        stop()
      }
    }
  }, [resortId])
}

export const useStatusColor = (status?: MetricStatusType, delta?: number) => {
  const theme = useTheme()
  let color =
    status === 'success'
      ? theme.palette.success.main
      : status === 'danger'
      ? theme.palette.error.main
      : status === 'neutral'
      ? theme.palette.text.primary
      : theme.palette.primary.main

  if (delta) {
    color = delta > 0 ? theme.palette.success.main : theme.palette.error.main
  }

  return color
}
